import { Button, Chip, Grid, TextField, Typography } from "@mui/material"
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { AlertView } from "./alert";
import { AxiosError } from "axios";
import { getApiAccessToken } from "../auth/auth";
import { addGroupUsers } from "../api/groups";
import { Group } from "../model/group";
import { addUsers, getUsers } from "../api/users";
import { NewUser, User } from '../model/user';
import { Loader } from "./loader";
import { ProgramUser } from "../model/program";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../reducers/store";
import { addGroupUsersAsync } from "../reducers/userSlice";

export interface UserInputProps {
    group: Group | null
}

export const UserInputs: React.FC<UserInputProps> = (props: UserInputProps) => {
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const dispatch: AppDispatch = useDispatch();
    
    const currentGroupUsers: User[] = useSelector((state: RootState) => state.users.currentGroupUsers);

    const [errorMessage, setError] = useState<string | null>(null);

    const [newUsers, setNewUsers] = useState<NewUser[]>([]);
    const [canSubmit, setCanSubmit] = useState<boolean>(true);

    const [loading, setLoading] = useState<boolean>(false);

    const processEmailInputs = (input: string) => {
        const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
      
        const potentialEmails = input.split(/[\n, ]/);
        
        const emails = potentialEmails.filter(item => item.match(emailPattern));

        let existingUsers = currentGroupUsers.filter(user => emails.includes(user.email))
        if (existingUsers.length > 0) {
            setError(`User${ existingUsers.length > 1 ? "s": ""} with email: ${ existingUsers.map(user => user.email).join(",")} already exist${ existingUsers.length > 1 ? "": "s"}`)
            return
        } else {
            setError(null)
        }

        setNewUsers(emails.map(e => ({ email: e })))
    }

    const handleSubmitUsers = async (newUsers: NewUser[]) => {
        try {
            if (props.group == null) {
                console.log("unexpected client is undefined")
                return
            }

            let accessToken = await getApiAccessToken(instance, activeAccount)


            let users: NewUser[] = []
            for(let u of newUsers) {
                users.push({
                    email: u.email,
                    firstName: u.firstName,
                    lastName: u.lastName,
                    department: u.department,
                })
            }

            let addedUsers = await addUsers(accessToken, users);

            dispatch(addGroupUsersAsync({accessToken, groupId: props.group.id, users: addedUsers}))

            setNewUsers([])
            setCanSubmit(true)
        } catch (error) {
            if (error instanceof AxiosError) {
                let code = Number(error?.status)

                if (code >= 400 && code < 500) {
                    setError(error?.message)
                } else {
                    setError(error.message)
                }

            } else if (error instanceof Error) {
                console.error(error);
            }
            setCanSubmit(true)
        }
    }

    const parseCsv = (text: string): NewUser[] => {
        const lines = text.trim().split(/\r?\n/).filter(line => line.trim());
        if (lines.length === 0) return [];

        const parseLine = (line: string): string[] => {
            const values: string[] = [];
            let current = '';
            let inQuotes = false;
            for (const ch of line) {
                if (ch === '"') {
                    inQuotes = !inQuotes;
                } else if (ch === ',' && !inQuotes) {
                    values.push(current.trim());
                    current = '';
                } else {
                    current += ch;
                }
            }
            values.push(current.trim());
            return values;
        };

        const firstLine = parseLine(lines[0]);
        const hasHeader = firstLine.some(col => /email/i.test(col));
        const headers = hasHeader ? firstLine.map(h => h.toLowerCase()) : null;

        const getColIndex = (names: string[]): number => {
            if (!headers) return -1;
            for (let i = 0; i < headers.length; i++) {
                if (names.includes(headers[i])) return i;
            }
            return -1;
        };

        const emailIdx = headers
            ? getColIndex(['email', 'e-mail', 'mail'])
            : 0;
        const firstNameIdx = headers
            ? getColIndex(['firstname', 'first_name', 'first name', 'fname'])
            : 1;
        const lastNameIdx = headers
            ? getColIndex(['lastname', 'last_name', 'last name', 'lname'])
            : 2;
        const deptIdx = headers
            ? getColIndex(['department', 'dept', 'org', 'organization', 'organisation'])
            : 3;

        const startRow = hasHeader ? 1 : 0;
        const results: NewUser[] = [];

        for (let i = startRow; i < lines.length; i++) {
            const cols = parseLine(lines[i]);

            const email = emailIdx >= 0 && emailIdx < cols.length ? cols[emailIdx] : '';
            if (!email) continue;

            const firstName = firstNameIdx >= 0 && firstNameIdx < cols.length ? cols[firstNameIdx] : undefined;
            const lastName = lastNameIdx >= 0 && lastNameIdx < cols.length ? cols[lastNameIdx] : undefined;
            const department = deptIdx >= 0 && deptIdx < cols.length ? cols[deptIdx] : undefined;

            results.push({
                email,
                firstName: firstName || undefined,
                lastName: lastName || undefined,
                department: department || undefined,
            });
        }

        return results;
    }

    const handleCsvClick = () => {
        setLoading(true);
        setTimeout(() => {
            setLoading(false)
        }, 2000);
    }

    const handleCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target?.result as string;
                const parsed = parseCsv(text);

                const existingUsers = currentGroupUsers.filter(user => 
                    parsed.some(p => p.email === user.email)
                );
                if (existingUsers.length > 0) {
                    setError(`User${ existingUsers.length > 1 ? "s": ""} with email: ${ existingUsers.map(user => user.email).join(",")} already exist${ existingUsers.length > 1 ? "": "s"}`)
                    setLoading(false);
                    return;
                } else {
                    setError(null);
                }

                setNewUsers(parsed);
            };
            reader.readAsText(file);
            event.target.value = '';
        }

        setLoading(false); 
    }

    const getChipLabel = (u: NewUser): string => {
        const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
        if (name) {
            return `${name} (${u.email})`;
        }
        return u.email;
    }

    return(
        <Grid container direction="column" sx={{ marginBottom: 5 }}  alignItems="left" justifyContent="left" textAlign="left">
            <AlertView error={errorMessage}/>
            <Grid container direction="row" spacing={2} sx={{ marginTop: 1 }}>
                <Grid item>
                    <TextField
                        id="outlined-multiline-flexible"
                        label="Add user emails"
                        sx={{ maxWidth: 650, minWidth: 400 }}
                        multiline
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            processEmailInputs(event.target.value)
                        }}
                    />
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        component="label"
                        sx={{ maxWidth: 200, height: '56px' }}
                    >
                        {loading ? <Loader width={20} height={20}/>: 
                        "Upload .CSV"}
                        <input
                            type="file"
                            accept=".csv"
                            hidden
                            onClick={handleCsvClick}
                            onChange={handleCsvUpload}
                        />
                    </Button>
                </Grid>
            </Grid>
            <Grid container direction="row" sx={{ marginBottom: 2 }}>
                {
                    newUsers.map(u => {
                        return(
                            <Chip
                            key={u.email}
                            label={getChipLabel(u)}
                            variant="outlined" 
                            sx={{ maxWidth: 400, flexShrink: 1, margin: 1 }}
                        />
                        )
                    })
                }
            </Grid>
            {
                newUsers.length > 0 ? 
                    <Button 
                        variant="outlined"
                        disabled={!canSubmit}
                        onClick={() => {
                            handleSubmitUsers(newUsers)
                            setCanSubmit(false)
                        }}
                        sx={{ maxWidth: 200, marginBottom: 2 }}>
                            Submit
                    </Button> : 
                    <></>
            }
        </Grid>
    )
}